import { useState, useRef, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import {
  LayoutGrid,
  PlusSquare,
  Layers,
  Shirt,
  Images,
  Upload,
  CalendarDays,
  Settings as SettingsIcon,
  LogOut,
  Building2,
  Users,
  ChevronDown,
  Check,
  Plus,
  Sparkles,
} from "lucide-react";
import { Logo, LogoMark } from "@/components/Logo";

const links = [
  { to: "/", label: "Aperçu", icon: LayoutGrid, end: true, id: "overview" },
  { to: "/assistant", label: "NATYE-IA", icon: Sparkles, id: "assistant" },
  { to: "/composer", label: "Nouveau Post", icon: PlusSquare, id: "composer" },
  { to: "/studio", label: "Studio IA", icon: Shirt, id: "studio" },
  { to: "/lot", label: "Lot IA", icon: Layers, id: "batch" },
  { to: "/galerie", label: "Galerie Produits", icon: Images, id: "gallery" },
  { to: "/mediatheque", label: "Médiathèque", icon: Upload, id: "media" },
  { to: "/calendrier", label: "Calendrier", icon: CalendarDays, id: "calendar" },
  { to: "/clients", label: "Marques Clients", icon: Building2, id: "clients" },
];

function ClientSwitcher() {
  const { clients, activeClient, setActive } = useClient();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="client-switcher"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-3 border border-white/15 rounded-sm px-3 py-2 hover:bg-white/[0.05] transition-colors min-w-[200px]"
      >
        <span
          className="h-3 w-3 rounded-sm shrink-0"
          style={{ background: activeClient?.color || "#14342B" }}
        />
        <span className="text-sm font-bold truncate flex-1 text-left">
          {activeClient?.name || "Aucune marque"}
        </span>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-full bg-[#111111] border border-white/15 rounded-sm z-30 py-1">
          {clients.map((c) => (
            <button
              key={c.id}
              data-testid={`switch-client-${c.id}`}
              onClick={() => {
                setActive(c.id);
                setOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/[0.05] transition-colors text-left"
            >
              <span
                className="h-3 w-3 rounded-sm shrink-0"
                style={{ background: c.color }}
              />
              <span className="text-sm truncate flex-1">{c.name}</span>
              {activeClient?.id === c.id && (
                <Check className="h-3.5 w-3.5 text-accent" />
              )}
            </button>
          ))}
          <button
            onClick={() => {
              setOpen(false);
              navigate("/clients");
            }}
            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/[0.05] transition-colors text-left border-t border-white/10 text-muted-foreground"
          >
            <Plus className="h-4 w-4" />
            <span className="text-sm">Ajouter une marque</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navLinks = [...links];
  if (user?.role === "admin")
    navLinks.push({ to: "/equipe", label: "Équipe", icon: Users, id: "team" });

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-16 lg:w-64 border-r border-white/10 flex flex-col fixed h-screen bg-background z-20">
        <div className="h-16 flex items-center px-4 lg:px-6 border-b border-white/10">
          <div className="hidden lg:block">
            <Logo size={30} />
          </div>
          <div className="lg:hidden">
            <LogoMark size={28} />
          </div>
        </div>

        <nav className="flex-1 py-4">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 lg:px-6 py-3 text-sm font-medium transition-colors duration-150 border-l-2 ${
                  isActive
                    ? "border-accent bg-white/[0.03] text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-white/[0.02]"
                }`
              }
            >
              <l.icon className="h-[18px] w-[18px] shrink-0" />
              <span className="hidden lg:inline">{l.label}</span>
            </NavLink>
          ))}
          <NavLink
            to="/parametres"
            data-testid="nav-settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 lg:px-6 py-3 text-sm font-medium transition-colors duration-150 border-l-2 ${
                isActive
                  ? "border-accent bg-white/[0.03] text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-white/[0.02]"
              }`
            }
          >
            <SettingsIcon className="h-[18px] w-[18px] shrink-0" />
            <span className="hidden lg:inline">Paramètres</span>
          </NavLink>
        </nav>

        <div className="border-t border-white/10 p-4">
          <div className="hidden lg:block mb-3">
            <p className="text-xs font-medium truncate">{user?.email}</p>
            <p className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground">
              {user?.role === "admin" ? "Administrateur" : "Membre"}
            </p>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex items-center gap-2 text-muted-foreground hover:text-accent text-sm transition-colors"
          >
            <LogOut className="h-[18px] w-[18px]" />
            <span className="hidden lg:inline">Déconnexion</span>
          </button>
          <NavLink
            to="/legal"
            data-testid="nav-legal"
            className="hidden lg:block mt-3 text-[10px] tracking-[0.15em] uppercase text-muted-foreground hover:text-foreground transition-colors"
          >
            Légal · Mentions · RGPD
          </NavLink>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-16 lg:ml-64 min-h-screen flex flex-col">
        {/* Top bar with client switcher */}
        <div className="h-14 border-b border-white/10 flex items-center justify-between px-6 lg:px-8 bg-background sticky top-0 z-10">
          <ClientSwitcher />
          <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">
            Marque active
          </span>
        </div>
        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
